<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DrawerMenuContent from "../DrawerMenuContent.vue";
import BlockAttributesPromptModal from "../BlockAttributesPromptModal.vue";
import ObjectsSearchField from "./ObjectsSearchField.vue";
import ObjectsCategoryChips from "./ObjectsCategoryChips.vue";
import ObjectsSection from "./ObjectsSection.vue";
import ObjectsCategoryItems from "./ObjectsCategoryItems.vue";
import {
  OBJECT_CATEGORIES,
  useObjectsCatalog,
  type ObjectCategory,
} from "@/composable/useObjectsCatalog";
import { useBlockPicking } from "@/composable/useBlockPicking";

/**
 * Onglet Objets (ex-onglets Blocs et Ouvrages) : tout ce qui se pose sur le
 * plan, en 4 catégories — blocs de la légende, blocs déjà dans le dessin, blocs
 * prédéfinis Inspexio, parties d'ouvrage paramétrées. Une recherche commune en
 * haut, un filtre par catégorie, et par défaut un aperçu de chacune.
 */
const {
  query,
  isSearching,
  selectedCategory,
  selectCategory,
  objects,
  counts,
} = useObjectsCatalog();

const {
  isArmed,
  attributesPrompt,
  showAttributesModal,
  onAttributesConfirm,
  selectDRBlock,
  selectDwgBlock,
  selectLegendBlock,
} = useBlockPicking();

// Autre catégorie ou autre recherche : la liste repart du haut (« Voir tout »
// se touche souvent après avoir fait défiler l'aperçu).
const content = ref<InstanceType<typeof DrawerMenuContent> | null>(null);
watch([selectedCategory, query], () => content.value?.scrollToTop());

/** Aperçu par catégorie dans la vue « Tout » (sans recherche). */
const PREVIEW_LIMIT: Record<ObjectCategory, number> = {
  legend: 5,
  drawing: 5,
  dr: 6,
  assets: 6,
};

const isOverview = computed(() => selectedCategory.value === "all");

/** Vue « Tout » : les catégories non vides ; sinon la catégorie choisie. */
const shownCategories = computed<ObjectCategory[]>(() =>
  isOverview.value
    ? OBJECT_CATEGORIES.filter((c) => counts.value[c] > 0)
    : [selectedCategory.value as ObjectCategory],
);

/** La recherche montre tout ce qu'elle trouve ; sinon un aperçu en vue « Tout ». */
function limitFor(category: ObjectCategory): number | undefined {
  return isOverview.value && !isSearching.value
    ? PREVIEW_LIMIT[category]
    : undefined;
}

function isTruncated(category: ObjectCategory): boolean {
  const limit = limitFor(category);
  return limit !== undefined && counts.value[category] > limit;
}

function onPick(category: ObjectCategory, name: string) {
  if (category === "legend") selectLegendBlock(name);
  else if (category === "drawing") selectDwgBlock(name);
  else if (category === "dr") selectDRBlock(name);
}

/** Message quand la vue est vide (catégorie vide, ou recherche sans résultat). */
const emptyMessage = computed(() => {
  const nothingShown = shownCategories.value.every(
    (c) => counts.value[c] === 0,
  );
  if (!nothingShown) return null;
  if (isSearching.value) {
    return { key: "canvas:settings.objects.noResult", query: query.value };
  }
  if (selectedCategory.value === "legend") {
    return { key: "canvas:settings.blocks.empty.legend" };
  }
  if (selectedCategory.value === "drawing") {
    return { key: "canvas:settings.blocks.empty.drawing" };
  }
  return { key: "canvas:settings.objects.empty" };
});
</script>

<template>
  <DrawerMenuContent
    ref="content"
    :label="$t('canvas:settings.objects.title')"
    :description="$t('canvas:settings.objects.description')"
  >
    <template #header>
      <ObjectsSearchField v-model="query" />
      <ObjectsCategoryChips
        :model-value="selectedCategory"
        :counts="counts"
        @update:model-value="selectCategory"
      />
    </template>

    <template v-if="isOverview">
      <ObjectsSection
        v-for="category in shownCategories"
        :key="category"
        :title="$t(`canvas:settings.objects.categories.${category}`)"
        :count="counts[category]"
        :truncated="isTruncated(category)"
        @see-all="selectCategory(category)"
      >
        <ObjectsCategoryItems
          :category="category"
          :objects="objects"
          :limit="limitFor(category)"
          :is-armed="isArmed"
          @pick="onPick"
        />
      </ObjectsSection>
    </template>

    <ObjectsCategoryItems
      v-else-if="!emptyMessage"
      :category="shownCategories[0]"
      :objects="objects"
      :is-armed="isArmed"
      @pick="onPick"
    />

    <div v-if="emptyMessage" class="objects-empty">
      <small>{{ $t(emptyMessage.key, { query: emptyMessage.query }) }}</small>
    </div>

    <BlockAttributesPromptModal
      v-if="attributesPrompt"
      v-model="showAttributesModal"
      :block-name="attributesPrompt.blockName"
      :attributes="attributesPrompt.attributes"
      :initial-values="attributesPrompt.initialValues"
      @confirm="onAttributesConfirm"
    />
  </DrawerMenuContent>
</template>

<style lang="scss" scoped>
.objects-empty {
  padding: 1rem 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
}
</style>
